import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, Circle, Truck, UserX, Download, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return '—';
  }
};

const StatusPill = ({ status, notRiding }) => {
  if (notRiding) {
    return (
      <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">
        <UserX className="w-3 h-3 mr-1" /> Absent
      </Badge>
    );
  }
  if (status === 'delivered') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Delivered
      </Badge>
    );
  }
  if (status === 'onboard') {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">
        <Truck className="w-3 h-3 mr-1" /> On Van
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border border-slate-200">
      <Circle className="w-3 h-3 mr-1" /> No-show
    </Badge>
  );
};

const DriverHistoryDialog = ({ open, onClose, driver }) => {
  const { getAuthHeaders } = useAuth();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState([]);

  const load = useCallback(async () => {
    if (!driver?.id) return;
    setLoading(true);
    try {
      const [hRes, dRes] = await Promise.all([
        axios.get(`${API}/drivers/${driver.id}/history?date=${date}`, getAuthHeaders()),
        axios.get(`${API}/drivers/${driver.id}/history/dates`, getAuthHeaders()),
      ]);
      setHistory(hRes.data);
      setDates(dRes.data.dates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load history');
    } finally {
      setLoading(false);
    }
  }, [driver, date, getAuthHeaders]);

  useEffect(() => {
    if (open) load();
     
  }, [open, date, driver?.id]);

  const shiftDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const exportCsv = () => {
    if (!history?.entries) return;
    const rows = [
      ['Stop', 'Child', 'Classroom', 'Direction', 'Status', 'Absent', 'Picked up', 'Delivered', 'Pickup address', 'Dropoff address'].join(','),
      ...history.entries.map((e) =>
        [
          e.stop_order ?? '',
          `"${e.name}"`,
          `"${e.classroom || ''}"`,
          e.direction || '',
          e.status,
          e.not_riding ? 'yes' : 'no',
          formatTime(e.onboard_at),
          formatTime(e.delivered_at),
          `"${e.pickup_address || ''}"`,
          `"${e.dropoff_address || ''}"`,
        ].join(','),
      ),
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `van-manifest-${driver?.name?.replace(/\s+/g, '_') || 'driver'}-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="driver-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-primary" />
            Van Manifest History — {driver?.name}
          </DialogTitle>
          <DialogDescription>
            Trip log for DCF compliance. Shows every child on the manifest with pickup/delivery timestamps.
          </DialogDescription>
        </DialogHeader>

        {/* Date picker + nav */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="icon" variant="outline" onClick={() => shiftDate(-1)} data-testid="history-prev-day" aria-label="Previous day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-40"
            data-testid="history-date-input"
          />
          <Button size="icon" variant="outline" onClick={() => shiftDate(1)} data-testid="history-next-day" aria-label="Next day">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {dates.length > 0 && dates[0] !== date && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setDate(dates[0])}
              data-testid="history-latest-day"
            >
              Jump to most recent ({dates[0]})
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!history?.entries?.length}
              data-testid="history-export-csv"
            >
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        {history && (
          <div className="grid grid-cols-4 gap-2" data-testid="history-stats">
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <div className="text-xl font-bold">{history.stats.total}</div>
              <div className="text-xs text-muted-foreground">On manifest</div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <div className="text-xl font-bold text-emerald-700">{history.stats.delivered}</div>
              <div className="text-xs text-muted-foreground">Delivered</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <div className="text-xl font-bold text-slate-600">{history.stats.absent}</div>
              <div className="text-xs text-muted-foreground">Absent</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <div className="text-xl font-bold text-amber-700">{history.stats.no_show}</div>
              <div className="text-xs text-muted-foreground">No-show</div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : !history || history.entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="history-empty">
            No van activity on {date}.
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="mobile-cards w-full text-sm">
                <thead className="bg-muted text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Child</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Classroom</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">On Van</th>
                    <th className="text-left px-3 py-2">Delivered</th>
                    <th className="text-left px-3 py-2 hidden lg:table-cell">Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {history.entries.map((e) => (
                    <tr key={e.child_id} data-testid={`history-row-${e.child_id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{e.stop_order ?? '—'}</td>
                      <td className="px-3 py-2 font-medium">{e.name}</td>
                      <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">{e.classroom || '—'}</td>
                      <td className="px-3 py-2"><StatusPill status={e.status} notRiding={e.not_riding} /></td>
                      <td className="px-3 py-2 tabular-nums">{formatTime(e.onboard_at)}</td>
                      <td className="px-3 py-2 tabular-nums">{formatTime(e.delivered_at)}</td>
                      <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground truncate max-w-[220px]">
                        {e.direction === 'dropoff' ? e.dropoff_address : e.pickup_address}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {history?.entries?.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Times shown in local browser timezone. Rows sorted by stop order.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverHistoryDialog;
